export type Color = "gray" | "brown" | "orange" | "yellow" | "green" | "blue" | "purple" | "pink" | "red";
export const COLORS: Color[] = ["gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"];

export type EntityType = "epic" | "sprint" | "task";
export type Role = "owner" | "member" | "viewer";
export type PropertyType = "select" | "multi_select" | "date";
export type StageKind = "normal" | "in_progress" | "qa" | "done";

export interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface Project {
  id: string;
  name: string;
  key: string;
  description: string;
  taskSeq: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListItem extends Project {
  role: Role;
  memberCount: number;
  epicCount: number;
  taskCount: number;
}

export interface PropertyOption {
  id: string;
  label: string;
  color: Color;
  position: number;
}

export interface Property {
  id: string;
  entityType: EntityType;
  name: string;
  type: PropertyType;
  position: number;
  options: PropertyOption[];
}

export interface Member {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  role: Role;
  joinedAt: string;
}

export interface Stage {
  id: string;
  projectId: string;
  name: string;
  color: Color;
  kind: StageKind;
  position: number;
}

export interface ListItem {
  id: string;
  projectId: string;
  name: string;
  color: Color;
  position: number;
}

export interface ProjectBundle {
  project: Project;
  role: Role;
  properties: Property[];
  members: Member[];
  stages: Stage[];
  sprintStatuses: ListItem[];
  assigneeStatuses: ListItem[];
}

/** propertyId -> option ids, or a single YYYY-MM-DD for date properties */
export type Values = Record<string, string[]>;

// BlockNote document JSON
export type Body = unknown[] | null;

export interface EpicListItem {
  id: string;
  projectId: string;
  title: string;
  position: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  sprintCount: number;
  taskCount: number;
  doneCount: number;
  values: Values;
}

export interface Epic {
  id: string;
  projectId: string;
  title: string;
  body: Body;
  position: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  values: Values;
}

export interface SprintListItem {
  id: string;
  projectId: string;
  epicId: string;
  epicTitle: string;
  title: string;
  goal: string;
  statusId: string | null;
  startDate: string | null;
  endDate: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  taskCount: number;
  doneCount: number;
  values: Values;
}

export interface Sprint extends Omit<SprintListItem, "taskCount" | "doneCount"> {
  body: Body;
}

export interface Assignee {
  userId: string;
  statusId: string | null;
  assignedAt: string;
}

export interface TimeEntry {
  userId: string;
  closedSeconds: number;
  openSince: string | null;
}

export interface TaskListItem {
  id: string;
  projectId: string;
  number: number;
  epicId: string;
  sprintId: string | null;
  stageId: string;
  title: string;
  position: number;
  inProgressAt: string | null;
  qaReachedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  prLinkCount: number;
  values: Values;
  assignees: Assignee[];
  time: TimeEntry[];
}

export interface Session {
  id: string;
  taskId: string;
  userId: string;
  startedAt: string;
  endedAt: string | null;
}

export interface PRLink {
  id: string;
  taskId: string;
  url: string;
  createdBy: string | null;
  createdAt: string;
}

export interface Task extends Omit<TaskListItem, "commentCount" | "prLinkCount"> {
  body: Body;
  sessions: Session[];
  prLinks: PRLink[];
}

export interface Comment {
  id: string;
  projectId: string;
  entityType: EntityType;
  entityId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface Activity {
  id: string;
  projectId: string;
  entityType: EntityType;
  entityId: string;
  actorId: string | null;
  action: string;
  data: Record<string, unknown>;
  createdAt: string;
  title?: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  actorId: string | null;
  projectId: string;
  projectName: string;
  entityType: EntityType;
  entityId: string;
  kind: "mentioned" | "commented" | "assigned" | "unassigned" | "stage_changed";
  data: { title?: string; number?: number; key?: string; snippet?: string; from?: string; to?: string };
  readAt: string | null;
  createdAt: string;
}

export interface FileLink {
  entityType: EntityType;
  entityId: string;
  title: string;
}

export interface ProjectFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  description: string;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
  links: FileLink[];
}

export interface SearchResult {
  entityType: EntityType;
  id: string;
  title: string;
  number: number | null;
  epicId: string | null;
  updatedAt: string;
}
